import * as React from "react";
import { BiPlus, BiSearch } from "react-icons/bi";
import { toaster } from "../ui/toaster";
import { useEffect, useState } from "react";
import { useWebHelper } from "@viabl/echo-web-client-core";
import { DialogBody, DialogContent, DialogFooter, DialogRoot, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Dialog, Text, useDisclosure, HStack, Input, Table, TableHeader, Spinner, Flex, InputAddon, Stack, Center, Box } from '@chakra-ui/react';
import { IBundle } from "../../pages/echoRoleBundles"
import { AiFillCaretLeft, AiFillCaretRight } from "react-icons/ai";
import { IoMdRefresh } from "react-icons/io";
import { useColorMode } from "../ui/color-mode";
import { Button } from "../ui/button";
import { FaCheckCircle, FaPlus, FaMinus } from "react-icons/fa";
import RolesEditor from "../echoRoles/echoRolesEditor";
import { useBreakpointValue } from "@chakra-ui/react";

interface IProps {
    cb: Function,
    existingBundle: IBundle,
}

interface IRole {
    id: string
    role: string
    is_in_bundle: boolean
}

export default function BundleAddRoles(props: IProps) {
    const { onOpen, onClose } = useDisclosure();
    const [id, setId] = useState<string>("")
    const [rows, setRows] = useState<any>([])
    const [page, setPage] = useState<number>(0)
    const [search, setSearch] = useState<string>("")
    const [debouncedSearch, setDebouncedSearch] = useState<string>("")
    const [loading, setLoading] = useState<boolean>(false);
    const [nextPageNo, setNextPageNo] = useState<number>(0);
    const [maxPages, setMaxPages] = useState<number>(0);
    const [pendingRoleIds, setPendingRoleIds] = useState<Record<string, boolean>>({});
    const [open, setOpen] = useState<boolean>(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const { colorMode, toggleColorMode } = useColorMode();

    const webHelpers = useWebHelper();

    const pageSize = useBreakpointValue({
        base: 6,
        md: 7,
        xl: 12,
    }, { fallback: "base" }) ?? 6;

    const handleClickOpen = () => {
        onOpen();
        setOpen(true);
        setSearch("");
        setPage(0)
        setErrors({})
        setId(props.existingBundle.id)
    }

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearch(value);
        setPage(0);
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedSearch(search);
        }, 300);

        return () => clearTimeout(timeout);
    }, [search]);

    function updateTable(query: string = search) {
        setLoading(true)
        setNextPageNo(page);
        webHelpers.GetAsync("/api/roles/search?page=" + page + "&pageSize=" + pageSize + "&query=" + query + "&bundleId=" + props.existingBundle.id + "&showAll=true", "auth").then((data: any) => {
            if (data === undefined) {
                toaster.create({
                    title: `${data}: Unable to fetch bundles from the API`,
                    type: "error",
                });
            } else {
                setRows(data.results);
                setMaxPages(data.max_pages);
                setLoading(false);
            }
        }
        ).catch((error) => {
            setLoading(false);
            toaster.create({
                title: `${error.status}: Unable to fetch bundles from the API`,
                type: "error",
            });
        });
    }

    const onForwardClick = () => {
        setPage(page + 1)
    }

    const onBackClick = () => {
        setPage(page - 1)
    }

    const onRefreshClick = () => {
        setPage(0)
        updateTable();
    }

    useEffect(() => {
        if (open) {
            updateTable(debouncedSearch)
        }
    }, [page, open, pageSize, debouncedSearch])

    const updateRowState = (roleId: string, isInBundle: boolean) => {
        setRows((currentRows: IRole[]) =>
            currentRows.map((role: IRole) =>
                role.id === roleId
                    ? { ...role, is_in_bundle: isInBundle }
                    : role
            )
        );
    };

    const handleAdd = async (roleId: string) => {
        setPendingRoleIds((currentPending) => ({ ...currentPending, [roleId]: true }));

        try {
            await webHelpers.PutAsync("/api/rolebundles/" + id + "/role/" + roleId, "auth");
            updateRowState(roleId, true);

            toaster.create({
                title: `Role added to bundle`,
                type: "success",
            });
        } catch (error: any) {
            toaster.create({
                title: `${error?.status ?? "Request failed"}: Unable to add role`,
                type: "error",
            });
        } finally {
            setPendingRoleIds((currentPending) => {
                const nextPending = { ...currentPending };
                delete nextPending[roleId];
                return nextPending;
            });
        }
    }

    const handleRemove = async (roleId: string) => {
        setPendingRoleIds((currentPending) => ({ ...currentPending, [roleId]: true }));

        try {
            await webHelpers.DeleteNoResultAsync("/api/rolebundles/" + id + "/role/" + roleId, "auth");
            updateRowState(roleId, false);

            toaster.create({
                title: `Role removed from bundle`,
                type: "success",
            });
        } catch (error: any) {
            toaster.create({
                title: `${error?.status ?? "Request failed"}: Unable to remove role`,
                type: "error",
            });
        } finally {
            setPendingRoleIds((currentPending) => {
                const nextPending = { ...currentPending };
                delete nextPending[roleId];
                return nextPending;
            });
        }
    }

    const handleToggleRole = (roleId: string, isInBundle: boolean) => {
        if (isInBundle) {
            handleRemove(roleId);
        } else {
            handleAdd(roleId);
        }
    }

    const handleClose = () => {
        setOpen(false);
        onClose();
    }

    return (
        <>
            <DialogRoot
                open={open}
                aria-describedby="alert-dialog-slide-description"
                size={{ base: "cover", "xl": "xl" }}

                placement={"center"}
                lazyMount
            >
                <Dialog.Backdrop />
                <DialogTrigger>
                    <Button
                        able
                        size="sm"
                        onClick={handleClickOpen}>
                        <BiPlus /> Add Role
                    </Button>
                </DialogTrigger>
                <DialogContent marginTop={"0px"} marginBottom={"0px"}>
                    <DialogTitle>Add Roles</DialogTitle>
                    <DialogBody minHeight={"500px"} overflowY={"auto"}>
                        <Stack
                            direction={{ base: "column", lg: "row" }}
                            width={"80%"}
                            m={"auto"}
                            pt={4}
                            pb={8}
                            px={20}
                            align="center"
                            justify={{ base: "", lg: "space-between" }}
                        >
                            <Flex marginLeft={"10px"} mt={2} verticalAlign={"middle"} direction={"row"} >
                                <InputAddon
                                    justifyContent={"center"}
                                    height={"30px"}
                                    marginTop={0}
                                    borderColor={"backgroundLightGrey"}
                                    background={"backgroundLightGrey"}
                                >
                                    <BiSearch size={"18px"} />
                                </InputAddon>
                                <Input
                                    bg={"backgroundDarkGrey"}
                                    height={"30px"}
                                    placeholder={" Search Bar"}
                                    paddingLeft={"10px"}
                                    value={search}
                                    onChange={(e) => handleSearchChange(e)}
                                    width={"200px"}
                                />
                            </Flex>
                            <HStack>
                                <Button
                                    size="sm"
                                    onClick={onRefreshClick}
                                    alignSelf={"right"}
                                >
                                    <IoMdRefresh />
                                </Button>
                                <Button
                                    disabled={(page === 0) ? true : false}
                                    size="sm"
                                    onClick={onBackClick}
                                    alignSelf={"right"}
                                >
                                    <AiFillCaretLeft />
                                </Button>
                                <Button
                                    disabled={(page >= maxPages - 1) ? true : false}
                                    size="sm"
                                    onClick={onForwardClick}
                                    alignSelf={"right"}
                                >
                                    <AiFillCaretRight />
                                </Button>

                                <RolesEditor cb={updateTable} existingRole={null} intent={"create"} />
                            </HStack>
                        </Stack>
                        <Table.Root
                            variant={"outline"}
                            textAlign="center"
                            m={"auto"}
                            maxW={"80%"}
                        >
                            {loading ?
                                <Flex
                                    direction={"row"}
                                    justifyContent={"center"}
                                    width={"100%"}
                                    bgColor={colorMode === "light" ? "backgroundDarkGrey" : "backgroundGrey"}
                                    marginTop={{ base: "170px", lg: "100px" }}
                                >
                                    <Spinner
                                        color={colorMode === "light" ? "AbleBlue" : "AbleYellow"}
                                        marginTop={"10px"}
                                        marginBottom={"100px"}
                                        size={"xl"}
                                        display={(nextPageNo < maxPages) && nextPageNo !== 1 ? "flex" : "none"}
                                    />
                                </Flex>
                                :
                                <>
                                    {
                                        rows.length === 0 ?
                                            <Text textAlign={"center"} py={30}>No Bundles Found!</Text>
                                            :
                                            <>
                                                <TableHeader>
                                                    <Table.Row>
                                                        <Table.ColumnHeader>Role</Table.ColumnHeader>
                                                        <Table.ColumnHeader textAlign={"right"}>Function</Table.ColumnHeader>
                                                    </Table.Row>
                                                </TableHeader>

                                                <Table.Body>
                                                    {rows.map((role: IRole) =>
                                                        <Table.Row key={role.id}>
                                                            <Table.Cell width={{ base: 200, lg: 400 }}>
                                                                <HStack gap={4}>
                                                                    {role.role}{role.is_in_bundle ? <FaCheckCircle size={18} /> : <></>}
                                                                </HStack>
                                                            </Table.Cell>
                                                            <Table.Cell ml={{ base: 0, lg: 250 }} mr={{ base: 0, lg: 100 }} textAlign={"right"}>
                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => handleToggleRole(role.id, role.is_in_bundle)}
                                                                    overrideBgColor={role.is_in_bundle}
                                                                    bg={"red"}
                                                                    disabled={pendingRoleIds[role.id] === true}
                                                                    aria-busy={pendingRoleIds[role.id] === true}
                                                                    loading={pendingRoleIds[role.id]}
                                                                >
                                                                    {role.is_in_bundle ? <FaMinus /> : <FaPlus />}
                                                                </Button>
                                                            </Table.Cell>
                                                        </Table.Row>
                                                    )}
                                                </Table.Body>
                                            </>
                                    }
                                </>
                            }
                        </Table.Root>
                        <Box pt={2} width={"30%"} textAlign={"center"} m={"auto"} >Page {page + 1} of {maxPages}</Box>
                    </DialogBody>
                    <DialogFooter>
                        <DialogTrigger>
                            <Button able onClick={handleClose} colorScheme="red" marginLeft={"10px"}>Close</Button>
                        </DialogTrigger>
                    </DialogFooter>
                </DialogContent>
            </DialogRoot>
        </>
    );
}